import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The choosable typefaces. All are stacks that resolve locally — a hosted
 * form must never fetch a remote font, for both speed and privacy.
 */
export const FONT_STACKS = {
    default: undefined, // the brand font, inherited from the theme
    system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    serif: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
    mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
    rounded: '"SF Pro Rounded", ui-rounded, "Nunito", "Segoe UI", sans-serif',
    humanist: '"Optima", "Gill Sans", "Gill Sans MT", Candara, sans-serif',
    slab: '"Rockwell", "Roboto Slab", "Courier Bold", Georgia, serif',
    condensed: '"HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif',
};
/** Human names for the picker — the keys are storage, not copy. */
export const FONT_LABELS = {
    default: "Koaform (default)",
    system: "System sans",
    serif: "Serif",
    mono: "Monospace",
    rounded: "Rounded",
    humanist: "Humanist",
    slab: "Slab serif",
    condensed: "Condensed",
};
export const fontStack = (font) => font ? FONT_STACKS[font] : undefined;
/**
 * The rating scales.
 *
 * Drawn as SVG paths, not text glyphs. A 40px "★" carries only ~21px of ink
 * inside its 40px line box, so a rating looked both too small for its font
 * size AND further from its question than every other control — the phantom
 * space above the glyph read as extra margin. An SVG fills the box it is
 * given, which makes size and spacing say what they mean.
 */
export const RATING_SHAPES = [
    { shape: "star", label: "Stars" },
    { shape: "heart", label: "Hearts" },
    { shape: "number", label: "Numbers" },
];
const STAR_PATH = "M12 2.6l2.9 6.06 6.6.9-4.8 4.6 1.2 6.55L12 17.6l-5.9 3.1 1.2-6.55-4.8-4.6 6.6-.9z";
const HEART_PATH = "M12 21s-7.5-4.7-9.6-9.2C.9 8.4 2.6 4.6 6.2 3.9c2.1-.4 4.2.6 5.3 2.3 1.1-1.7 3.2-2.7 5.3-2.3 3.6.7 5.3 4.5 3.8 7.9C19.5 16.3 12 21 12 21z";
/** The rating mark at a given size. `number` scales are rendered by the caller. */
export const RatingIcon = ({ shape, className = "", }) => (_jsx("svg", { viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", className: className, children: _jsx("path", { d: shape === "heart" ? HEART_PATH : STAR_PATH }) }));
/**
 * Uploaded theme images are stored as app-relative /i/… paths so published
 * schemas survive the API moving hosts; pasted URLs stay absolute.
 */
export const resolveThemeUrl = (url, apiUrl) => (url?.startsWith("/") ? `${apiUrl}${url}` : url);
/**
 * Turns a form's theme into CSS-variable overrides.
 *
 * Tailwind 4 compiles `bg-primary-600` to `var(--color-primary-600)`, so
 * setting the variables inline on a wrapper restyles every primary-* class
 * inside it — one accent value themes buttons, selected choices, focus rings
 * and the rating stars without a second styling system. The lighter and darker
 * steps are derived with color-mix so any accent gets a coherent scale.
 */
export const themeVars = (theme) => {
    const accent = theme?.accent;
    if (!accent)
        return {};
    const mix = (pct, base) => `color-mix(in srgb, ${accent} ${pct}%, ${base})`;
    return {
        "--color-primary-50": mix(7, "white"),
        "--color-primary-100": mix(14, "white"),
        "--color-primary-300": mix(45, "white"),
        "--color-primary-600": accent,
        "--color-primary-700": mix(86, "black"),
    };
};
/** The banner image above a themed form. Renders nothing without a URL. */
export const FormBanner = ({ url }) => url ? (_jsx("img", { src: url, alt: "", className: "mb-4 h-44 w-full rounded-[20px] object-cover" })) : null;
/**
 * Banner and logo as one masthead.
 *
 * The logo is pulled up so it sits over the bottom ~35% of the banner, the
 * way a profile header does — separately stacked they read as two unrelated
 * images with a gap between them. The overlap is a fixed -20px against a
 * 56px logo rather than a percentage, because percentage margins resolve
 * against the *container width*, which would make the overlap jump around
 * with the viewport.
 *
 * Shared by the renderer, the builder canvas and the preview so all three
 * show the same masthead.
 */
export const FormMedia = ({ bannerUrl, logoUrl, className = "", }) => {
    if (!bannerUrl && !logoUrl)
        return null;
    return (_jsxs("div", { className: className, children: [bannerUrl ? (_jsx("img", { src: bannerUrl, alt: "", className: "h-44 w-full rounded-[20px] object-cover" })) : null, logoUrl ? (_jsx("img", { src: logoUrl, alt: "", className: `relative block h-14 w-14 rounded-full border-[3px] border-surface bg-surface object-cover shadow-[0_4px_14px_-4px_rgba(12,26,46,0.35)] ${bannerUrl ? "-mt-5 ml-6" : ""}` })) : null] }));
};
//# sourceMappingURL=formTheme.js.map